import { applyI18n, t } from '../../i18n';
import { createHelpTip } from '../../help/helpTips';
import { iconImg, type IconName } from '../../ui/icons';
import { openFilesContextMenu, type FilesContextMenuItem } from '../../ui/filesContextMenu';

export const SEDR_SIDEBAR_COLLAPSED_KEY = 'shareweb:sedr-sidebar-collapsed';
export const SEDR_MOBILE_LAYOUT_MQ = '(max-width: 768px)';

export interface SedrChromeTab {
  id: string;
  labelKey: string;
  disabled?: boolean;
}

export function isSedrMobileLayout(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SEDR_MOBILE_LAYOUT_MQ).matches;
}

/** Mobile always uses the collapsed (panel-menu) chrome; desktop follows the saved preference. */
export function effectiveSidebarCollapsed(preference: boolean): boolean {
  return isSedrMobileLayout() || preference;
}

export function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SEDR_SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SEDR_SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function applySidebarCollapsed(
  layout: HTMLElement,
  sidebar: HTMLElement,
  collapsed: boolean,
): void {
  layout.classList.toggle('sedr-room-layout--nav-hidden', collapsed);
  sidebar.hidden = collapsed;
  sidebar.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
  // Inline styles beat the workspace ready rule (`.sedr-workspace--open.sedr-workspace--ready
  // .sedr-room-layout { 220px 1fr }`). Without this, a hidden sidebar still leaves a 220px
  // track and the main pane is auto-placed into it.
  if (collapsed) {
    layout.style.gridTemplateColumns = 'minmax(0, 1fr)';
    layout.style.gap = '0';
  } else {
    layout.style.gridTemplateColumns = '';
    layout.style.gap = '';
  }
}

function isFullscreenElement(target: HTMLElement): boolean {
  return document.fullscreenElement === target;
}

async function toggleFullscreen(target: HTMLElement): Promise<void> {
  try {
    if (isFullscreenElement(target)) {
      await document.exitFullscreen();
    } else {
      await target.requestFullscreen();
    }
  } catch {
    // Browser may deny fullscreen without a user gesture or support.
  }
}

function createChromeIconButton(options: {
  icon: IconName;
  labelKey: string;
  pressed?: boolean;
  onClick: () => void;
}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'help-tip sedr-room-chrome__action';
  const label = t(options.labelKey);
  btn.setAttribute('data-i18n-aria-label', options.labelKey);
  btn.setAttribute('data-i18n-title', options.labelKey);
  btn.setAttribute('aria-label', label);
  btn.title = label;
  if (options.pressed !== undefined) {
    btn.setAttribute('aria-pressed', options.pressed ? 'true' : 'false');
  }
  btn.append(iconImg(options.icon, 14));
  btn.addEventListener('click', options.onClick);
  return btn;
}

/** Active panel label; becomes a menu button when the left nav is hidden. */
export function createActivePanelControl(options: {
  activeTab: string;
  tabs: SedrChromeTab[];
  navHidden: boolean;
  onSelect: (tabId: string) => void;
}): HTMLElement {
  const active = options.tabs.find((tab) => tab.id === options.activeTab) ?? options.tabs[0];
  const labelKey = active?.labelKey ?? 'sedr.nav.dashboard';

  if (!options.navHidden) {
    const label = document.createElement('span');
    label.className = 'sedr-room-chrome__panel';
    label.setAttribute('data-i18n', labelKey);
    label.textContent = t(labelKey);
    return label;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'sedr-room-chrome__panel sedr-room-chrome__panel--menu';
  button.setAttribute('aria-haspopup', 'menu');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('data-i18n-aria-label', 'sedr.chrome.switchPanel');
  button.setAttribute('data-i18n-title', 'sedr.chrome.switchPanel');
  button.setAttribute('aria-label', t('sedr.chrome.switchPanel'));
  button.title = t('sedr.chrome.switchPanel');

  const text = document.createElement('span');
  text.setAttribute('data-i18n', labelKey);
  text.textContent = t(labelKey);
  button.append(text, iconImg('chevron-down', 14));

  button.addEventListener('click', () => {
    button.setAttribute('aria-expanded', 'true');
    const items: FilesContextMenuItem[] = options.tabs.map((tab) => ({
      id: tab.id,
      labelKey: tab.labelKey,
      disabled: tab.disabled,
      action: () => options.onSelect(tab.id),
    }));
    openFilesContextMenu({
      anchorRect: button.getBoundingClientRect(),
      items,
    });
    // Context menu closes itself; reset expanded when menu is gone.
    const observer = new MutationObserver(() => {
      if (!document.querySelector('[data-files-context-menu="true"]')) {
        button.setAttribute('aria-expanded', 'false');
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });

  applyI18n(button);
  return button;
}

export function renderSedrRoomChromeActions(options: {
  fullscreenTarget: HTMLElement;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  helpTopic: string;
  /** Defaults to hidden on the mobile layout breakpoint. */
  showSidebarToggle?: boolean;
  /** Defaults to hidden on the mobile layout breakpoint. */
  showFullscreenToggle?: boolean;
}): { fragment: DocumentFragment; cleanup: () => void } {
  const fragment = document.createDocumentFragment();
  const mobile = isSedrMobileLayout();
  const showSidebarToggle = options.showSidebarToggle ?? !mobile;
  const showFullscreenToggle = options.showFullscreenToggle ?? !mobile;

  if (showSidebarToggle) {
    const sidebarBtn = createChromeIconButton({
      icon: 'panel-left',
      labelKey: options.sidebarCollapsed ? 'sedr.chrome.showNav' : 'sedr.chrome.hideNav',
      // Highlighted when nav is hidden (collapsed = active).
      pressed: options.sidebarCollapsed,
      onClick: options.onToggleSidebar,
    });
    fragment.append(sidebarBtn);
  }

  let syncFullscreenButton: (() => void) | null = null;
  if (showFullscreenToggle) {
    const fullscreenBtn = createChromeIconButton({
      icon: isFullscreenElement(options.fullscreenTarget) ? 'minimize' : 'maximize',
      labelKey: isFullscreenElement(options.fullscreenTarget)
        ? 'sedr.chrome.exitFullscreen'
        : 'sedr.chrome.enterFullscreen',
      pressed: isFullscreenElement(options.fullscreenTarget),
      onClick: () => {
        void toggleFullscreen(options.fullscreenTarget);
      },
    });
    fragment.append(fullscreenBtn);

    syncFullscreenButton = () => {
      const active = isFullscreenElement(options.fullscreenTarget);
      const labelKey = active ? 'sedr.chrome.exitFullscreen' : 'sedr.chrome.enterFullscreen';
      const label = t(labelKey);
      fullscreenBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
      fullscreenBtn.setAttribute('data-i18n-aria-label', labelKey);
      fullscreenBtn.setAttribute('data-i18n-title', labelKey);
      fullscreenBtn.setAttribute('aria-label', label);
      fullscreenBtn.title = label;
      fullscreenBtn.replaceChildren(iconImg(active ? 'minimize' : 'maximize', 14));
    };

    document.addEventListener('fullscreenchange', syncFullscreenButton);
  }

  fragment.append(createHelpTip(options.helpTopic));

  return {
    fragment,
    cleanup: () => {
      if (syncFullscreenButton) {
        document.removeEventListener('fullscreenchange', syncFullscreenButton);
      }
    },
  };
}

export function navLabelKeyForTab(tab: string): string {
  switch (tab) {
    case 'dashboard':
      return 'sedr.nav.dashboard';
    case 'phases':
      return 'sedr.nav.phases';
    case 'compliance':
      return 'sedr.nav.compliance';
    case 'evidence':
      return 'sedr.nav.evidence';
    case 'access':
      return 'sedr.nav.access';
    case 'reports':
      return 'sedr.nav.reports';
    case 'audit':
      return 'sedr.nav.audit';
    case 'settings':
      return 'sedr.nav.settings';
    default:
      return 'sedr.nav.dashboard';
  }
}
